import {
  voucherVatExplainerUrl,
  type CompanyDetailsError,
  type CompanyDetailsErrors,
  type CompanyDetailsField,
} from '@shared/utils/giftVoucherBuyer';

/**
 * Form state for a company buyer's details — at checkout and on the "add company details"
 * page reached from the purchase confirmation email. Validation itself is
 * `normalizeCompanyDetails` in shared/; this file only holds what the two forms share.
 */

export type CompanyDetailsForm = Record<CompanyDetailsField, string>;

export interface CompanyFieldSpec {
  field: CompanyDetailsField;
  /** 'optional' prints "optional", 'recommended' prints "optional, recommended". */
  presence: 'required' | 'optional' | 'recommended';
  wide?: boolean;
  type?: 'text' | 'email';
  autoComplete?: string;
}

/** The fields in the order the form shows them. */
export const COMPANY_FORM_FIELDS: CompanyFieldSpec[] = [
  { field: 'legal_name', presence: 'required', wide: true, autoComplete: 'organization' },
  { field: 'vat_number', presence: 'recommended' },
  { field: 'registration_number', presence: 'optional' },
  { field: 'street', presence: 'required', wide: true, autoComplete: 'street-address' },
  { field: 'postal_code', presence: 'required', autoComplete: 'postal-code' },
  { field: 'city', presence: 'required', autoComplete: 'address-level2' },
  { field: 'country', presence: 'required', wide: true, autoComplete: 'country' },
  { field: 'billing_email', presence: 'required', wide: true, type: 'email', autoComplete: 'email' },
  { field: 'po_reference', presence: 'optional', wide: true },
];

export const emptyCompanyDetails = (defaults: { country?: string; billingEmail?: string } = {}): CompanyDetailsForm => ({
  legal_name: '',
  vat_number: '',
  registration_number: '',
  street: '',
  postal_code: '',
  city: '',
  country: (defaults.country || '').toUpperCase(),
  billing_email: defaults.billingEmail || '',
  po_reference: '',
});

/**
 * The billing email follows the buyer's own email until the buyer types in the field;
 * from then on it is theirs.
 */
export function syncBillingEmail(form: CompanyDetailsForm, buyerEmail: string | undefined, edited: boolean): CompanyDetailsForm {
  if (edited || !buyerEmail || form.billing_email === buyerEmail) return form;
  return { ...form, billing_email: buyerEmail };
}

/** Fills an empty country with the venue's, never overriding a choice. */
export function withDefaultCountry(form: CompanyDetailsForm, country: string | undefined): CompanyDetailsForm {
  if (form.country || !country) return form;
  return { ...form, country: country.toUpperCase() };
}

export function clearFieldError(errors: CompanyDetailsErrors, field: CompanyDetailsField): CompanyDetailsErrors {
  if (!errors[field]) return errors;
  const next = { ...errors };
  delete next[field];
  return next;
}

/** i18n key for a field's error. */
export function companyErrorKey(field: CompanyDetailsField, code: CompanyDetailsError | undefined): string | null {
  if (!code) return null;
  if (code === 'required') return 'companyDetails.error_required';
  if (field === 'billing_email') return 'companyDetails.error_email';
  if (field === 'vat_number') return 'companyDetails.error_vat';
  if (field === 'country') return 'companyDetails.error_country';
  return 'companyDetails.error_invalid';
}

/** Which register the "Company registration no." hint names, from the venue's country. */
export function registrationHintKey(venueCountry: string | undefined): string | null {
  const country = (venueCountry || '').toUpperCase();
  if (country === 'BE') return 'companyDetails.registration_hint_be';
  if (country === 'NL') return 'companyDetails.registration_hint_nl';
  return null;
}

/**
 * The in-app route of the "Gift vouchers and VAT" explainer, in the shop's language
 * (Dutch for Dutch, English otherwise) with the venue's country preselected.
 * Derived from the public URL printed on the PDFs, so both stay the same.
 */
export function explainerPath(locale: string | undefined, venueCountry: string | undefined): string {
  const url = new URL(voucherVatExplainerUrl(locale, venueCountry));
  return `${url.pathname}${url.search}`;
}

/** Every ISO 3166-1 alpha-2 code; names come from `Intl.DisplayNames` in the shop's language. */
const ISO_COUNTRIES =
  'AD AE AF AG AI AL AM AO AQ AR AS AT AU AW AX AZ BA BB BD BE BF BG BH BI BJ BL BM BN BO BQ BR BS BT BV BW BY BZ ' +
  'CA CC CD CF CG CH CI CK CL CM CN CO CR CU CV CW CX CY CZ DE DJ DK DM DO DZ EC EE EG EH ER ES ET FI FJ FK FM FO FR ' +
  'GA GB GD GE GF GG GH GI GL GM GN GP GQ GR GS GT GU GW GY HK HM HN HR HT HU ID IE IL IM IN IO IQ IR IS IT JE JM JO ' +
  'JP KE KG KH KI KM KN KP KR KW KY KZ LA LB LC LI LK LR LS LT LU LV LY MA MC MD ME MF MG MH MK ML MM MN MO MP MQ MR ' +
  'MS MT MU MV MW MX MY MZ NA NC NE NF NG NI NL NO NP NR NU NZ OM PA PE PF PG PH PK PL PM PN PR PS PT PW PY QA RE RO ' +
  'RS RU RW SA SB SC SD SE SG SH SI SJ SK SL SM SN SO SR SS ST SV SX SY SZ TC TD TF TG TH TJ TK TL TM TN TO TR TT TV ' +
  'TW TZ UA UG UM US UY UZ VA VC VE VG VI VN VU WF WS YE YT ZA ZM ZW';

export const COUNTRY_CODES: string[] = ISO_COUNTRIES.split(' ');

export interface CountryOption {
  code: string;
  name: string;
}

/**
 * Countries for the dropdown, sorted by name in `intlTag`, with the venue's country (and
 * its neighbours the shop mostly sells to) first.
 */
export function countryOptions(
  intlTag: string,
  venueCountry?: string
): { preferred: CountryOption[]; others: CountryOption[] } {
  let display: Intl.DisplayNames | null = null;
  try {
    display = new Intl.DisplayNames([intlTag, 'en'], { type: 'region' });
  } catch {
    display = null;
  }
  const nameOf = (code: string): string => {
    try {
      return display?.of(code) || code;
    } catch {
      return code;
    }
  };
  const all = COUNTRY_CODES.map((code) => ({ code, name: nameOf(code) })).sort((a, b) =>
    a.name.localeCompare(b.name, intlTag)
  );
  const venue = (venueCountry || '').toUpperCase();
  const preferred = [venue, 'BE', 'NL', 'LU', 'FR', 'DE'].filter(
    (code, index, list) => code && COUNTRY_CODES.includes(code) && list.indexOf(code) === index
  );
  return {
    preferred: preferred
      .map((code) => all.find((option) => option.code === code))
      .filter((option): option is CountryOption => Boolean(option)),
    others: all.filter((option) => !preferred.includes(option.code)),
  };
}
